export type LinkifyPart =
  | { type: 'text'; value: string }
  | { type: 'link'; value: string; href: string };

/** Strip common trailing punctuation from a URL token (kept as following text). */
export function stripTrailingFromUrlToken(raw: string): { core: string; trailing: string } {
  let core = raw;
  let trailing = '';
  const stripChars = new Set(['.', ',', ';', ':', '!', '?', ')', ']', '}', '>']);
  while (core.length > 0) {
    const ch = core[core.length - 1]!;
    if (stripChars.has(ch)) {
      trailing = ch + trailing;
      core = core.slice(0, -1);
      continue;
    }
    break;
  }
  return { core, trailing };
}

export function hrefForLinkToken(core: string): string | null {
  const c = core.trim();
  if (!c) return null;
  const low = c.toLowerCase();
  if (low.startsWith('javascript:') || low.startsWith('data:') || low.startsWith('vbscript:')) {
    return null;
  }
  if (low.startsWith('mailto:')) {
    return /^mailto:\S+$/i.test(c) ? c : null;
  }
  if (low.startsWith('http://') || low.startsWith('https://')) {
    return c;
  }
  if (low.startsWith('www.')) {
    return `https://${c}`;
  }
  return null;
}

/**
 * Split plain text into alternating text and http(s)/www/mailto link spans.
 * Safe for rendering as text nodes + anchors (no HTML injection).
 */
export function linkifyPlainTextParts(input: string): LinkifyPart[] {
  if (!input) return [];
  const parts: LinkifyPart[] = [];
  let last = 0;
  const r = /(?:https?:\/\/[^\s<]+|www\.[^\s<]+|mailto:\S+)/gi;
  let m: RegExpExecArray | null;
  while ((m = r.exec(input)) !== null) {
    if (m.index > last) {
      parts.push({ type: 'text', value: input.slice(last, m.index) });
    }
    const full = m[0];
    const { core, trailing } = stripTrailingFromUrlToken(full);
    const href = hrefForLinkToken(core);
    if (href) {
      parts.push({ type: 'link', value: core, href });
      if (trailing) {
        parts.push({ type: 'text', value: trailing });
      }
    } else {
      parts.push({ type: 'text', value: full });
    }
    last = m.index + full.length;
  }
  if (last < input.length) {
    parts.push({ type: 'text', value: input.slice(last) });
  }
  if (parts.length === 0) {
    return [{ type: 'text', value: input }];
  }
  return mergeAdjacentTextParts(parts);
}

function mergeAdjacentTextParts(parts: LinkifyPart[]): LinkifyPart[] {
  const out: LinkifyPart[] = [];
  for (const p of parts) {
    const prev = out[out.length - 1];
    if (prev && prev.type === 'text' && p.type === 'text') {
      prev.value += p.value;
    } else if (p.type === 'text') {
      out.push({ type: 'text', value: p.value });
    } else {
      out.push(p);
    }
  }
  return out;
}
